import React, { useEffect, useState } from 'react';
import { View, Text, Button } from "../../../Components/Themed";
import { ActivityIndicator } from 'react-native';
import { useColors } from '../../../constants/useColors';
import { BetRouterApi } from '../../api';
import { Icons, SearchBox } from '../../../Components';
import Switch from '../../../Components/Switch';
import moment from 'moment-mini';
import type {
    RouterContestProps,
    MarketContestProps,
    RouterMarketProps
} from '../../types';

type AutoFillData = {
    router_contests: RouterContestProps[];
    market_contests: MarketContestProps[];
    loading: boolean;
    searchValue: string;
    router_markets: RouterMarketProps[];
};

const AutoFillView = () => {
    const Colors = useColors();

    const [data, setData] = useState<AutoFillData>({
        router_contests: [],
        market_contests: [],
        loading: true,
        searchValue: '',
        router_markets: []
    });

    useEffect(() => {
        loadAutoFillData();
        loadRouterMarkets();
    }, []);

    const loadAutoFillData = async () => {
        setData(prev => ({ ...prev, loading: true }));
        try {
            const result = await BetRouterApi.BetRouter.ContestApi.getAutoFillContests();
            setData(prev => ({
                ...prev,
                router_contests: result.router_contests || [],
                market_contests: result.market_contests || [],
                loading: false
            }));
        } catch (error) {
            console.error('Failed to load auto-fill data:', error);
            alert('Error loading auto-fill data: ' + (error as any).message);
            setData(prev => ({ ...prev, loading: false }));
        }
    };

    const loadRouterMarkets = async () => {
        try {
            const markets = await BetRouterApi.BetRouter.MarketApi.getRouterMarkets();
            setData(prev => ({ ...prev, router_markets: Array.isArray(markets) ? markets : [] }));
        } catch (error) {
            console.error('Failed to load router markets:', error);
        }
    };

    const toggleContestAutoFill = async (contest: RouterContestProps) => {
        try {
            setData(prev => ({ ...prev, loading: true }));
            await BetRouterApi.BetRouter.ContestApi.updateRouterContest(
                contest.router_contest_id,
                { auto_fill: !contest.auto_fill }
            );
            await loadAutoFillData();
        } catch (error) {
            console.error('Error updating contest auto-fill:', error);
            alert('Error updating contest: ' + (error as any).message);
            setData(prev => ({ ...prev, loading: false }));
        }
    };

    const toggleMarketContestAutoFill = async (marketContest: MarketContestProps) => {
        try {
            setData(prev => ({ ...prev, loading: true }));
            await BetRouterApi.BetRouter.MarketApi.updateMarketContest(
                marketContest.market_contest_id,
                { auto_fill: !marketContest.auto_fill }
            );
            await loadAutoFillData();
        } catch (error) {
            console.error('Error updating market contest auto-fill:', error);
            alert('Error updating market contest: ' + (error as any).message);
            setData(prev => ({ ...prev, loading: false }));
        }
    };

    const getMarketName = (router_market_id: string) => {
        const market = data.router_markets.find(m => m.router_market_id === router_market_id);
        return market?.market_label || router_market_id;
    };

    const getMarketContestsForContest = (router_contest_id: string) => {
        return data.market_contests.filter(mc => mc.router_contest_id === router_contest_id);
    };

    const filteredContests = data.router_contests.filter(contest =>
        data.searchValue === '' ||
        contest.contest_label.toLowerCase().includes(data.searchValue.toLowerCase()) ||
        contest.contest_label.toLowerCase().includes(data.searchValue.toLowerCase()) ||
        contest.router_contest_id.toLowerCase().includes(data.searchValue.toLowerCase())
    );

    if (data.loading) {
        return (
            <View transparent style={{ padding: 40, alignItems: 'center' }}>
                <ActivityIndicator size="large" color={Colors.text.h1} />
            </View>
        );
    }

    return (
        <View transparent style={{ flex: 1 }}>
            {/* Header */}
            <View transparent style={{ marginBottom: 24 }}>
                <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View transparent>
                        <Text theme='h1' size={24}>Auto-Fill Manager</Text>
                        <Text theme='description' size={14} style={{ marginTop: 8 }}>
                            View and manage all contests and markets that are currently auto-filling
                        </Text>
                    </View>
                    <Button
                        type='action'
                        title='Refresh'
                        onPress={loadAutoFillData}
                    />
                </View>
            </View>

            {/* Stats Cards */}
            <View transparent style={{ flexDirection: 'row', gap: 16, marginBottom: 24 }}>
                <View type='body' style={{ flex: 1, padding: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light }}>
                    <Text theme='description' size={12}>CONTESTS WITH AUTO-FILL</Text>
                    <Text theme='h1' size={32} style={{ marginTop: 8 }}>{data.router_contests.length}</Text>
                    <Text theme='description' size={11} style={{ marginTop: 4 }}>
                        Total router contests
                    </Text>
                </View>

                <View type='body' style={{ flex: 1, padding: 16, borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light }}>
                    <Text theme='description' size={12}>MARKETS WITH AUTO-FILL</Text>
                    <Text theme='h1' size={32} style={{ marginTop: 8 }}>{data.market_contests.length}</Text>
                    <Text theme='description' size={11} style={{ marginTop: 4 }}>
                        Total market auto-fills
                    </Text>
                </View>
            </View>

            {/* Search Box */}
            <View transparent style={{ marginBottom: 16 }}>
                <SearchBox
                    placeholder="Search contests..."
                    onChange={(text) => setData(prev => ({ ...prev, searchValue: text }))}
                    hide_search_button
                />
            </View>

            {/* Contest List */}
            <View type='body' style={{ borderRadius: 8, borderWidth: 1, borderColor: Colors.borders.light, overflow: 'hidden' }}>
                <View type='header' style={{ padding: 16 }}>
                    <Text theme='h2' size={16}>Auto-Fill Contests</Text>
                    <Text theme='description' size={12} style={{ marginTop: 4 }}>
                        {filteredContests.length} contest{filteredContests.length !== 1 ? 's' : ''} with auto-fill enabled
                    </Text>
                </View>

                <View transparent style={{ padding: 16 }}>
                    {filteredContests.length === 0 ? (
                        <View transparent style={{ padding: 40, alignItems: 'center' }}>
                            <Icons.AlertIcon size={48} color={Colors.text.descriptionLight} />
                            <Text theme='description' size={14} style={{ marginTop: 16, textAlign: 'center' }}>
                                {data.searchValue
                                    ? 'No contests match your search'
                                    : 'No contests with auto-fill enabled'}
                            </Text>
                        </View>
                    ) : (
                        filteredContests.map((contest) => {
                            const marketContests = getMarketContestsForContest(contest.router_contest_id);
                            return (
                                <View
                                    key={contest.router_contest_id}
                                    transparent
                                    style={{
                                        marginBottom: 12,
                                        padding: 16,
                                        backgroundColor: Colors.views.header,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: Colors.borders.light
                                    }}
                                >
                                    {/* Contest Header */}
                                    <View transparent style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                        <View transparent style={{ flex: 1 }}>
                                            <Text theme='h2' size={16}>{contest.contest_label}</Text>
                                            <Text theme='description' size={12} style={{ marginTop: 4 }}>
                                                {contest.contest_label}
                                            </Text>
                                            <Text theme='description' size={11} style={{ marginTop: 4 }}>
                                                {moment(contest.scheduled_datetime).format('ddd, MMM D, YYYY [at] h:mm A')}
                                            </Text>
                                        </View>
                                        <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                            <Text theme='description' size={12}>Auto-fill:</Text>
                                            <Switch
                                                value={contest.auto_fill ?? false}
                                                switch_type='on_off'
                                                size={14}
                                                onChange={(_) => toggleContestAutoFill(contest)}
                                            />
                                        </View>
                                    </View>

                                    {/* Market Contests */}
                                    {marketContests.length > 0 && (
                                        <View transparent style={{ marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: Colors.borders.light }}>
                                            <Text theme='description' size={11} style={{ marginBottom: 8, textTransform: 'uppercase' }}>
                                                Markets with Auto-Fill ({marketContests.length})
                                            </Text>
                                            {marketContests.map((mc) => (
                                                <View
                                                    key={mc.market_contest_id}
                                                    transparent
                                                    style={{
                                                        flexDirection: 'row',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        paddingVertical: 8,
                                                        paddingHorizontal: 12,
                                                        backgroundColor: Colors.views.header,
                                                        borderRadius: 6,
                                                        marginBottom: 6
                                                    }}
                                                >
                                                    <View transparent style={{ flex: 1 }}>
                                                        <Text theme='h2' size={13}>
                                                            {getMarketName(mc.router_market_id)}
                                                        </Text>
                                                        <Text theme='description' size={10} style={{ marginTop: 2 }}>
                                                            {mc.market_contest_id}
                                                        </Text>
                                                    </View>
                                                    <View transparent style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                        <Text theme='description' size={11}>Auto-fill:</Text>
                                                        <Switch
                                                            value={mc.auto_fill ?? false}
                                                            switch_type='on_off'
                                                            size={12}
                                                            onChange={(_) => toggleMarketContestAutoFill(mc)}
                                                        />
                                                    </View>
                                                </View>
                                            ))}
                                        </View>
                                    )}
                                </View>
                            );
                        })
                    )}
                </View>
            </View>
        </View>
    );
};

export default AutoFillView;
